<template>
  <div>
    <div
      v-if="usingCorrectedViscosityRatio && showCorrectedRatioBadge"
      class="flex text-xs p-1 justify-content-end"
    >
      <Badge severity="danger">Uses corrected viscosity rate</Badge>
    </div>
    <TornadoChart
      v-bind="$attrs"
      :aspect-ratio="4"
      :x-unit="chartingData?.tdh.targetXUnit"
      reverse-y
      x-label="In Situ Flow Rate"
      :y-label="yLabel"
      :minimum-height-px="minimumHeightPx"
      :frequency-data-sets="chartingData?.tdh.operatingDataSets || []"
      :ror-data-sets="chartingData?.tdh.rorDataSets || []"
      :operating-points="operatingPoints || []"
      :additional-data-sets="[]"
      :draw-legend="drawLegend"
      :is-horizontal="isHorizontal"
      :is-static="isStatic"
      x-legend-label="In Situ Flow Rate"
      y-legend-label="TDH"
      :title="title"
    />
  </div>
</template>

<script setup lang="ts">
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { XrefPumps, Scenario } from "@/composables/forms/formTypes";
import TornadoChart from "./TornadoChart.vue";
import { useTaperedPumpTornadoChartingData } from "@/libs/charting/tornado/useTaperedPumpTornadoChartingData";
import {
  scenarioKey,
  sizingKey,
  scenarioCalculationsKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { injectStrict } from "@/utils/utils";
import { computed } from "vue";
import { computePumpLabel } from "@/models/equipment/pumps/properties";
import {
  PoundsPerSquareInchDifferential,
  PressureUnits
} from "@/libs/units/pressure";
import type { OperatingPoint } from "@/components/types.js";

const props = defineProps<{
  minFrequency: number;
  maxFrequency: number;
  minimumHeightPx: number;
  drawLegend?: boolean;
  isHorizontal?: boolean;
  isStatic?: boolean;
  xrefPump?: XrefPumps[number];
  scenario?: Scenario | null;
  showOperatingFrequency?: boolean;
  scenarioCalculations?: ScenarioCalculations;
  showCorrectedRatioBadge?: boolean;
}>();

const _scenario = props.scenario ?? injectStrict(scenarioKey);
const _sizing = injectStrict(sizingKey, null);
const _scenarioCalculations =
  props.scenarioCalculations ?? injectStrict(scenarioCalculationsKey);

const {
  getLiquidFlowUnit,
  getHydraulicPowerUnit,
  getLargeLengthUnit,
  getPressureUnit
} = useScenarioUnits(_scenario, _sizing);

const targetLiquidFlowUnit = getLiquidFlowUnit(
  "Target Liquid Rate.write.STB/D"
);
const targetPowerUnit = getHydraulicPowerUnit("Shaft Limit.convert.hp");
const targetLargeLengthUnit = getLargeLengthUnit("Required TDH.convert.Feet");
const targetPressureUnit = getPressureUnit(
  "Producing Bottomhole Pressure at Datum [Pwf].write.PSIA"
);
const title = computed(
  () =>
    `${computePumpLabel(
      props.xrefPump?.r_pump,
      props.xrefPump?.use_high_strength_shaft
    )} - ${props.xrefPump?.total_stages} stages`
);
const targetYUnit = computed(() => {
  // If it's an HPS scenario, the TDH is actually in pressure units, so use that unit for the y axis
  return chartingData.value?.tdh.targetYUnit;
});

const yLabel = computed(() => {
  const yUnit = targetYUnit.value;
  const isYUnitPSI =
    yUnit === PressureUnits.PoundsPerSquareInchAbsolute ||
    yUnit === PressureUnits.PoundsPerSquareInchGauge;
  if (_scenarioCalculations.isScenarioEspShore.value) {
    return `Total Dynamic Head (${
      targetLargeLengthUnit.value.inSituLabel ||
      targetLargeLengthUnit.value.shortLabel ||
      targetLargeLengthUnit.value.label
    })`;
  } else if (!isYUnitPSI) {
    return `Differential Pressure (${
      targetPressureUnit.value.inSituLabel ||
      targetPressureUnit.value.shortLabel ||
      targetPressureUnit.value.label
    })`;
  } else {
    return `Differential Pressure (${PoundsPerSquareInchDifferential.inSituLabel || PoundsPerSquareInchDifferential.shortLabel || PoundsPerSquareInchDifferential.label})`;
  }
});

const chartingData = computed(
  () =>
    props.xrefPump &&
    useTaperedPumpTornadoChartingData(
      props.minFrequency,
      props.maxFrequency,
      _scenarioCalculations,
      targetLiquidFlowUnit.value,
      targetPowerUnit.value,
      targetLargeLengthUnit.value,
      targetPressureUnit.value,
      props.xrefPump
    )
);

const operatingPoints = computed<OperatingPoint[]>(() =>
  (chartingData.value?.tdh.operatingPointData.operatingPoints || []).map(
    point => ({
      ...point,
      hideYDetails: point.label === "Intake"
    })
  )
);

const usingCorrectedViscosityRatio = computed(
  () => chartingData.value?.context.isRateCorrected
);
</script>
